import { get, uniq } from 'lodash';

import type { ITableColumn } from '../../components/Table/utils/types';
import type { IColumn } from '../types';
import type { ITableFilterState } from '../hooks/useLocalTableView';

type IFilterableColumn<T> = ITableColumn<T> &
  Pick<IColumn, 'filterable' | 'filterName'>;

const toFilterText = (value: unknown) =>
  value == null || value === '' ? undefined : String(value);

const filterPath = <T>({ filterName, key }: IFilterableColumn<T>) =>
  filterName || String(key);

/**
 * Adds antd filter dropdowns to `filterable` columns. Options are the distinct
 * values found at `filterName` (or the column key) across `rows`. There is no
 * `onFilter`: antd copies every row when it filters, so rows are filtered with
 * `filterRows` instead.
 */
export const applyColumnFilters = <T extends object>(
  columns: ITableColumn<T>[],
  rows: T[],
  filters: ITableFilterState
): ITableColumn<T>[] =>
  columns.map(column => {
    const filterable = column as IFilterableColumn<T>;
    if (!filterable.filterable) return column;

    const path = filterPath(filterable);
    const values = uniq(
      rows
        .map(row => toFilterText(get(row, path)))
        .filter((value): value is string => !!value)
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    return {
      ...column,
      filters: values.map(value => ({ text: value, value })),
      filterSearch: true,
      filteredValue: filters[String(column.key)] ?? null,
    };
  });

/** Keeps the rows matching every active column filter (any value per column). */
export const filterRows = <T extends object>(
  rows: T[],
  columns: ITableColumn<T>[],
  filters: ITableFilterState
): T[] => {
  const active = columns
    .map(column => ({
      path: filterPath(column as IFilterableColumn<T>),
      values: filters[String(column.key)],
    }))
    .filter(({ values }) => values && values.length);

  if (!active.length) return rows;

  return rows.filter(row =>
    active.every(({ path, values }) =>
      values!.includes(toFilterText(get(row, path)) as string)
    )
  );
};
